import { schoolYearOf, startOfDay, toDate } from './dates.ts';
import {
  type Activity,
  type AttendanceRecord,
  type AttendanceStats,
  type Baladin,
  type Budget,
  type FinanceCategory,
  type Id,
  type IsoDate,
  type Rsvp,
  type Transaction,
  type UniformOrderItem,
} from './types.ts';
import { uniformItemPickedUpCount, uniformOrderItemLabel } from './uniforms.ts';

/** Arrondi à une décimale, sans `-0`. */
function round1(value: number): number {
  const out = Math.round(value * 10) / 10;
  return out === 0 ? 0 : out;
}

export const EMPTY_ATTENDANCE_STATS: AttendanceStats = {
  total: 0,
  present: 0,
  absent: 0,
  rate: 0,
};

/**
 * Agrège des présences réelles.
 *
 * Deux états seulement depuis le retrait de « En retard » et « Excusé » : le
 * taux est donc simplement la part de présents sur le total encodé. Les lignes
 * non encodées ne figurent pas dans `records` et ne pèsent sur rien.
 */
export function computeAttendanceStats(records: AttendanceRecord[]): AttendanceStats {
  const stats = { ...EMPTY_ATTENDANCE_STATS, total: records.length };
  for (const record of records) {
    switch (record.status) {
      case 'present':
        stats.present += 1;
        break;
      case 'absent':
        stats.absent += 1;
        break;
    }
  }
  stats.rate = stats.total > 0 ? round1((stats.present / stats.total) * 100) : 0;
  return stats;
}

export function groupBy<T, K extends string>(items: T[], key: (item: T) => K): Map<K, T[]> {
  const out = new Map<K, T[]>();
  for (const item of items) {
    const k = key(item);
    const bucket = out.get(k);
    if (bucket) bucket.push(item);
    else out.set(k, [item]);
  }
  return out;
}

export interface PersonAttendanceSummary {
  personId: Id;
  stats: AttendanceStats;
  records: AttendanceRecord[];
}

export function attendanceByPerson(records: AttendanceRecord[]): PersonAttendanceSummary[] {
  return [...groupBy(records, (r) => r.personId)]
    .map(([personId, personRecords]) => ({
      personId,
      stats: computeAttendanceStats(personRecords),
      records: personRecords,
    }))
    .sort((a, b) => b.stats.rate - a.stats.rate);
}

export interface AttendanceTrendPoint {
  activityId: Id;
  date: IsoDate;
  label: string;
  stats: AttendanceStats;
}

/** Série chronologique du taux de présence, une entrée par activité. */
export function attendanceTrend(
  records: AttendanceRecord[],
  activities: Activity[],
): AttendanceTrendPoint[] {
  const byActivity = groupBy(records, (r) => r.activityId);
  const index = new Map(activities.map((a) => [a.id, a]));
  return [...byActivity]
    .flatMap(([activityId, activityRecords]) => {
      const activity = index.get(activityId);
      if (!activity) return [];
      return [
        {
          activityId,
          date: activity.startAt.slice(0, 10),
          label: activity.title,
          stats: computeAttendanceStats(activityRecords),
        },
      ];
    })
    .sort((a, b) => a.date.localeCompare(b.date));
}

export interface RsvpForecast {
  present: number;
  absent: number;
  uncertain: number;
  noAnswer: number;
  /** Effectif attendu : présents + moitié des incertains, arrondi. */
  expected: number;
  responseRate: number;
}

/**
 * Prévision d'effectif pour une activité. Les « incertains » comptent pour une
 * demi-présence : c'est l'hypothèse la plus utile pour dimensionner le matériel
 * et les équipes sans sous-estimer ni surestimer.
 */
export function computeRsvpForecast(rsvps: Rsvp[], totalBaladins: number): RsvpForecast {
  let present = 0;
  let absent = 0;
  let uncertain = 0;
  for (const rsvp of rsvps) {
    if (rsvp.status === 'present') present += 1;
    else if (rsvp.status === 'absent') absent += 1;
    else uncertain += 1;
  }
  const answered = present + absent + uncertain;
  return {
    present,
    absent,
    uncertain,
    noAnswer: Math.max(0, totalBaladins - answered),
    expected: Math.round(present + uncertain / 2),
    responseRate: totalBaladins > 0 ? round1((answered / totalBaladins) * 100) : 0,
  };
}

// ---------------------------------------------------------------------------
// Finances
// ---------------------------------------------------------------------------

export interface FinanceSummary {
  incomeCents: number;
  expenseCents: number;
  /**
   * Solde de la caisse : `(capitalDeDepart || 0) + recettes − dépenses`.
   * Sans le capital, le chiffre ne reflète que le mouvement, pas ce qu'il y a.
   */
  balanceCents: number;
  transactionCount: number;
  /** Capital de départ inclus dans `balanceCents`. */
  openingBalanceCents: number;
}

/**
 * Solde de la section. Un capital absent ou nul est traité comme zéro : la
 * caisse part alors des seules opérations encodées.
 */
export function sectionBalanceCents(
  openingBalanceCents: number | null | undefined,
  incomeCents: number,
  expenseCents: number,
): number {
  return (openingBalanceCents || 0) + incomeCents - expenseCents;
}

/**
 * Solde à afficher, compatible avec une API encore ancienne.
 *
 * Si le résumé porte déjà `openingBalanceCents`, `balanceCents` inclut le
 * capital : on le fait confiance. Sinon on l'ajoute ici, pour que Accueil et
 * Statistiques restent justes tant que Railway n'a pas redéployé.
 */
export function displayedBalanceCents(
  summary: {
    incomeCents: number;
    expenseCents: number;
    balanceCents: number;
    openingBalanceCents?: number | null;
  },
  settingsOpeningCents: number | null | undefined,
): number {
  if (typeof summary.openingBalanceCents === 'number') {
    return summary.balanceCents;
  }
  return sectionBalanceCents(settingsOpeningCents, summary.incomeCents, summary.expenseCents);
}

export function computeFinanceSummary(
  transactions: Transaction[],
  openingBalanceCents: number | null | undefined = 0,
): FinanceSummary {
  let incomeCents = 0;
  let expenseCents = 0;
  for (const t of transactions) {
    if (t.kind === 'income') incomeCents += t.amountCents;
    else expenseCents += t.amountCents;
  }
  const opening = openingBalanceCents || 0;
  return {
    incomeCents,
    expenseCents,
    openingBalanceCents: opening,
    balanceCents: sectionBalanceCents(opening, incomeCents, expenseCents),
    transactionCount: transactions.length,
  };
}

export interface CategoryBreakdown {
  categoryId: Id | null;
  categoryName: string;
  color: string;
  amountCents: number;
  share: number;
  count: number;
}

export function financeBreakdown(
  transactions: Transaction[],
  categories: FinanceCategory[],
  kind: 'income' | 'expense',
): CategoryBreakdown[] {
  const index = new Map(categories.map((c) => [c.id, c]));
  const filtered = transactions.filter((t) => t.kind === kind);
  const total = filtered.reduce((sum, t) => sum + t.amountCents, 0);

  // Un identifiant de catégorie que la liste fournie ne contient pas est traité
  // comme une absence de catégorie : sinon deux identifiants inconnus donnent
  // deux lots distincts portant le même libellé « Sans catégorie ». Le cas se
  // produit dès que les opérations sont chargées avant les catégories.
  const buckets = groupBy(filtered, (t) => (t.categoryId && index.has(t.categoryId) ? t.categoryId : '__none__'));

  return [...buckets]
    .map(([key, items]) => {
      const category = key === '__none__' ? undefined : index.get(key);
      const amountCents = items.reduce((sum, t) => sum + t.amountCents, 0);
      return {
        categoryId: category?.id ?? null,
        categoryName: category?.name ?? 'Sans catégorie',
        color: category?.color ?? '#7C7A72',
        amountCents,
        share: total > 0 ? round1((amountCents / total) * 100) : 0,
        count: items.length,
      };
    })
    .sort((a, b) => b.amountCents - a.amountCents);
}

export interface BudgetProgress {
  categoryId: Id;
  categoryName: string;
  color: string;
  budgetCents: number;
  spentCents: number;
  remainingCents: number;
  usage: number;
  overBudget: boolean;
}

export function budgetProgress(
  budgets: Budget[],
  transactions: Transaction[],
  categories: FinanceCategory[],
  period: string = schoolYearOf(),
): BudgetProgress[] {
  const index = new Map(categories.map((c) => [c.id, c]));
  const spendByCategory = new Map<Id, number>();
  for (const t of transactions) {
    if (t.kind !== 'expense' || !t.categoryId) continue;
    if (schoolYearOf(t.date) !== period) continue;
    spendByCategory.set(t.categoryId, (spendByCategory.get(t.categoryId) ?? 0) + t.amountCents);
  }

  return budgets
    .filter((b) => b.period === period)
    .map((budget) => {
      const category = index.get(budget.categoryId);
      const spentCents = spendByCategory.get(budget.categoryId) ?? 0;
      return {
        categoryId: budget.categoryId,
        categoryName: category?.name ?? 'Catégorie supprimée',
        color: category?.color ?? '#7C7A72',
        budgetCents: budget.amountCents,
        spentCents,
        remainingCents: budget.amountCents - spentCents,
        usage: budget.amountCents > 0 ? round1((spentCents / budget.amountCents) * 100) : 0,
        overBudget: spentCents > budget.amountCents,
      };
    })
    .sort((a, b) => b.usage - a.usage);
}

export interface MonthlyFinancePoint {
  month: string;
  incomeCents: number;
  expenseCents: number;
  /** Solde cumulé à la fin du mois. */
  runningBalanceCents: number;
}

/** Série mensuelle du solde, triée chronologiquement. */
export function monthlyFinanceSeries(
  transactions: Transaction[],
  openingBalanceCents: number | null | undefined = 0,
): MonthlyFinancePoint[] {
  const buckets = groupBy(transactions, (t) => t.date.slice(0, 7));
  const months = [...buckets.keys()].sort();
  let running = openingBalanceCents || 0;
  return months.map((month) => {
    const items = buckets.get(month) ?? [];
    const { incomeCents, expenseCents } = computeFinanceSummary(items);
    running += incomeCents - expenseCents;
    return { month, incomeCents, expenseCents, runningBalanceCents: running };
  });
}

// ---------------------------------------------------------------------------
// Formatage monétaire
// ---------------------------------------------------------------------------

const currencyFmt = new Intl.NumberFormat('fr-BE', { style: 'currency', currency: 'EUR' });

export function formatCents(cents: number): string {
  return currencyFmt.format(cents / 100);
}

/** Montant signé, pour les listes de transactions. */
export function formatSignedCents(cents: number, kind: 'income' | 'expense'): string {
  const sign = kind === 'income' ? '+' : '−';
  return `${sign} ${currencyFmt.format(cents / 100)}`;
}

/**
 * Parse une saisie utilisateur en centimes. Accepte « 12,50 », « 12.50 »,
 * « 1 234,56 » et « €12.50 ». Renvoie `null` si la saisie est inexploitable.
 */
export function parseAmountToCents(input: string): number | null {
  const cleaned = input
    .replace(/[€\s  ]/g, '')
    .replace(/\.(?=\d{3}\b)/g, '')
    .replace(',', '.');
  if (cleaned === '' || !/^-?\d*(\.\d*)?$/.test(cleaned)) return null;
  const value = Number.parseFloat(cleaned);
  if (Number.isNaN(value)) return null;
  return Math.round(Math.abs(value) * 100);
}

// ---------------------------------------------------------------------------
// Statistiques de section
// ---------------------------------------------------------------------------

export interface SectionStats {
  baladinCount: number;
  animateurCount: number;
  averageAge: number | null;
  activitiesThisYear: number;
  attendanceRate: number;
  gamesCount: number;
  photosCount: number;
  balanceCents: number;
}

export function averageAge(ages: Array<number | null>): number | null {
  const valid = ages.filter((a): a is number => a != null);
  if (valid.length === 0) return null;
  return round1(valid.reduce((sum, a) => sum + a, 0) / valid.length);
}

/** Nombre d'activités depuis le début de l'année scolaire en cours. */
export function countActivitiesThisSchoolYear(activities: Activity[], at: Date = new Date()): number {
  const year = schoolYearOf(at);
  return activities.filter((a) => schoolYearOf(a.startAt) === year).length;
}

/** Prochaine activité à venir (la plus proche dans le futur, hors annulées). */
export function nextActivity(activities: Activity[], at: Date = new Date()): Activity | null {
  const upcoming = activities
    .filter((a) => toDate(a.endAt).getTime() >= at.getTime())
    .sort((a, b) => a.startAt.localeCompare(b.startAt));
  return upcoming[0] ?? null;
}

/** Activités du jour, y compris celles déjà commencées. */
export function activitiesOn(activities: Activity[], day: Date): Activity[] {
  const dayStart = startOfDay(day).getTime();
  const dayEnd = dayStart + 86_400_000;
  return activities
    .filter((a) => {
      const start = toDate(a.startAt).getTime();
      const end = toDate(a.endAt).getTime();
      return start < dayEnd && end >= dayStart;
    })
    .sort((a, b) => a.startAt.localeCompare(b.startAt));
}

/** Ligne du consolidé fournisseur : produit × taille. */
export interface UniformOrderTotal {
  label: string;
  size: string;
  quantity: number;
  productId?: string;
}

/** Agrège les paniers de commande pour savoir quoi commander au fournisseur. */
export function aggregateUniformTotals(
  orders: Array<{ items: UniformOrderItem[] }>,
): UniformOrderTotal[] {
  const map = new Map<string, UniformOrderTotal>();
  for (const order of orders) {
    for (const item of order.items) {
      const label = uniformOrderItemLabel(item);
      const key = `${item.productId ?? item.article ?? label}::${item.size}`;
      const existing = map.get(key);
      if (existing) {
        existing.quantity += item.quantity;
      } else {
        map.set(key, {
          label,
          size: item.size,
          quantity: item.quantity,
          productId: item.productId,
        });
      }
    }
  }
  return [...map.values()].sort((a, b) => {
    const labelCmp = a.label.localeCompare(b.label, 'fr');
    if (labelCmp !== 0) return labelCmp;
    return a.size.localeCompare(b.size, 'fr', { numeric: true });
  });
}

/** Ligne du récapitulatif des articles restant à distribuer aux parents. */
export interface UniformRemainingTotal {
  label: string;
  size: string;
  totalQuantity: number;
  pickedUpQuantity: number;
  remainingQuantity: number;
  productId?: string;
}

/**
 * Calcule pour chaque produit et taille le nombre d’unités restant à donner aux parents,
 * en déduisant ce qui a déjà été retiré (totalement ou partiellement).
 */
export function aggregateUniformRemaining(
  orders: Array<{ items: UniformOrderItem[]; pickedUpAt?: string | null }>,
): UniformRemainingTotal[] {
  const map = new Map<string, UniformRemainingTotal>();
  for (const order of orders) {
    for (const item of order.items ?? []) {
      const label = uniformOrderItemLabel(item);
      const key = `${item.productId ?? item.article ?? label}::${item.size}`;
      const picked = uniformItemPickedUpCount(item, order.pickedUpAt);
      const remaining = Math.max(0, item.quantity - picked);
      const existing = map.get(key);
      if (existing) {
        existing.totalQuantity += item.quantity;
        existing.pickedUpQuantity += picked;
        existing.remainingQuantity += remaining;
      } else {
        map.set(key, {
          label,
          size: item.size,
          totalQuantity: item.quantity,
          pickedUpQuantity: picked,
          remainingQuantity: remaining,
          productId: item.productId,
        });
      }
    }
  }
  return [...map.values()].sort((a, b) => {
    const labelCmp = a.label.localeCompare(b.label, 'fr');
    if (labelCmp !== 0) return labelCmp;
    return a.size.localeCompare(b.size, 'fr', { numeric: true });
  });
}

/**
 * Identifie les baladins qui appartiennent à une fratrie au sein de la section.
 *
 * Deux enfants sont considérés de la même famille s'ils partagent :
 * - un même compte parent (`parentLinks`),
 * - une même adresse e-mail parent (`parent1Email` ou `parent2Email`),
 * - un même numéro de téléphone parent (`parent1Phone` ou `parent2Phone`),
 * - ou le même nom de famille ET la même adresse postale non vide.
 */
export function detectSiblingBaladinIds(
  baladins: Baladin[],
  parentLinks: Array<{ parentId: Id; baladinId: Id }> = [],
): Set<Id> {
  const active = baladins.filter((b) => !b.deletedAt);
  const activeIds = new Set(active.map((b) => b.id));
  const buckets = new Map<string, Set<Id>>();

  const addKey = (key: string, baladinId: Id) => {
    let set = buckets.get(key);
    if (!set) {
      set = new Set<Id>();
      buckets.set(key, set);
    }
    set.add(baladinId);
  };

  for (const link of parentLinks) {
    if (link.parentId && activeIds.has(link.baladinId)) {
      addKey(`parent:${link.parentId}`, link.baladinId);
    }
  }

  for (const b of active) {
    for (const rawEmail of [b.parent1Email, b.parent2Email]) {
      const email = (rawEmail ?? '').trim().toLowerCase();
      if (email.length >= 5 && email.includes('@')) {
        addKey(`email:${email}`, b.id);
      }
    }
    for (const rawPhone of [b.parent1Phone, b.parent2Phone]) {
      const digits = (rawPhone ?? '').replace(/\D+/g, '');
      if (digits.length >= 8) {
        addKey(`phone:${digits.slice(-8)}`, b.id);
      }
    }
    const lastName = b.lastName.trim().toLowerCase();
    const address = (b.address ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
    if (lastName && address.length >= 5) {
      addKey(`addr:${lastName}::${address}`, b.id);
    }
  }

  const siblings = new Set<Id>();
  for (const group of buckets.values()) {
    if (group.size >= 2) {
      for (const id of group) siblings.add(id);
    }
  }
  return siblings;
}

